'use client';

import * as React from 'react';

import { routes } from '@routes';
import { useScript, appleAuthHelpers } from 'react-apple-signin-auth';

import { Button } from '@components/button';
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { Input } from '@components/form/input';
import { PasswordInput } from '@components/form/password-input';
import { signIn } from '@lib/auth/client';
import {
  SocialLogin,
  SocialLoginType,
} from '@components/social-login/social-login';

type Props = { message?: string; redirectUrl?: string; onClose?: () => void };

export const Login: React.FC<Props> = ({
  message: _message,
  redirectUrl,
  onClose,
}) => {
  const [message, setMessage] = React.useState(_message);
  const [isSubmitting, setSubmitting] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  const onEmailChangeHandler = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(value);
  };

  const onPasswordChangeHandler = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(value);
  };

  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);

  return (
    <div className="flex flex-col items-center justify-center px-6 pb-8 pt-4 sm:px-8">
      <div className="flex flex-col text-center w-full">
        <h1 className="text-5xl sm:text-6xl font-medium tracking-tight text-primary-main leading-tight">
          Welcome back
        </h1>

        <p className="mt-4 text-lg text-primary-main/70">
          Don&apos;t have an account?{' '}
          <Link
            to={routes.signup()}
            onClick={onClose}
            className="font-semibold text-primary-main underline underline-offset-2 hover:text-secondary-main transition-colors"
          >
            Sign up
          </Link>
        </p>
      </div>

      <form
        className="mt-10 w-full space-y-5"
        onSubmit={async (event) => {
          setSubmitting(true);
          event.preventDefault();

          const user = await signIn('credentials', {
            email,
            password,
            redirect: false,
            callbackUrl: redirectUrl,
          });

          if (user?.error === 'account_blocked') {
            setMessage(
              'Your account has been suspended. Please contact support.'
            );
          } else if (user?.error) {
            setMessage('Incorrect email or password');
          } else {
            onClose?.();
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
          }

          setSubmitting(false);
        }}
      >
        {message && (
          <div className="rounded-xl border-[1.5px] border-error-main bg-error-light/30 px-4 py-3 text-center text-sm font-medium text-error-main">
            {message}
          </div>
        )}

        <div className="w-full space-y-4">
          <Input
            id="email"
            name="email"
            label="Email"
            autoComplete="email"
            value={email}
            onChange={onEmailChangeHandler}
            required
          />

          <PasswordInput
            id="password"
            name="password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={onPasswordChangeHandler}
            required
          />
        </div>

        <div className="flex items-center justify-end">
          <Link
            to={routes.passwordForgot}
            onClick={onClose}
            className="text-sm font-semibold text-primary-main/60 hover:text-primary-main transition-colors"
          >
            Forgot your password?
          </Link>
        </div>

        <div className="pt-1">
          <Button
            isFullWidth={true}
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="text-base"
          >
            Log In
          </Button>
        </div>
      </form>

      <div className="mt-8 w-full">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-400" />
          </div>
          <div className="relative flex justify-center">
            <Typography
              component="span"
              variant="body2"
              className="bg-white px-4 text-primary-main/50 text-sm"
            >
              or continue with
            </Typography>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-6 flex w-full flex-col items-center space-y-3">
        <SocialLogin
          social={SocialLoginType.Google}
          onClick={() =>
            signIn('google', redirectUrl ? { callbackUrl: redirectUrl } : undefined)
          }
          isSignUp={false}
        />

        {/* <SocialLogin
          social={SocialLoginType.Facebook}
          onClick={() => signIn('facebook')}
          isSignUp={false}
        /> */}
      </div>
    </div>
  );
};
